// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useSession } from '@common/authorization/context/Session';
import { type PropsWithChildren, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import PageFooter from '../components/layout/PageFooter';
import { PORTAL_IDLE_TIMEOUT } from './constants';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { useLocalLogout, useLogout } from './hooks/useLogout';
import { PortalHeader, TenantType } from './PortalHeader';
import { PortalPath } from './PortalRoutes';
import './PortalLayout.less';

const PortalLayout = ({ children }: PropsWithChildren) => {
  const navigateKeepSearch = useNavigateKeepSearch();
  const location = useLocation();

  const { tenantType } = useSession();

  const userLogout = useLogout();
  const localLogout = useLocalLogout();

  const handleSettings = () => {
    navigateKeepSearch(PortalPath.SETTINGS);
  };

  const handleLogout = async () => {
    if (tenantType === TenantType.TEST) {
      await localLogout();
    } else {
      await userLogout();
    }
  };

  const { startIdleTimer, stopIdleTimer } = useIdleTimeout(PORTAL_IDLE_TIMEOUT, handleLogout);

  useEffect(() => {
    startIdleTimer();
    return () => {
      stopIdleTimer();
    };
  }, [startIdleTimer, stopIdleTimer]);

  const isSettingsRoute = location.pathname.startsWith(PortalPath.SETTINGS);

  const scrollYOff = isSettingsRoute ? 'scroll-y-off' : 'content';

  return (
    <div className="portal-layout">
      {/* Header */}
      <PortalHeader
        userLogout={handleLogout}
        handleSettings={handleSettings}
        isSettingsRoute={isSettingsRoute}
      />

      {/* Main content */}
      <div className={`layout-content ${scrollYOff}`}>{children}</div>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default PortalLayout;
